import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { FaBell } from "react-icons/fa";
import { useAdminNotifications } from "../../Contexts/AdminNotificationContext";
import AdminNotificationItem from "../AdminNotificationItem/AdminNotificationItem";

type AdminNotificationBellProps = {
  // Icon colour classes, so the bell matches the navbar it sits in
  className?: string;
};

const AdminNotificationBell = ({ className = "text-black hover:text-primary-600" }: AdminNotificationBellProps) => {
  const navigate = useNavigate();
  const { unreadCount, latest, markAllAsRead } = useAdminNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const badge = unreadCount > 99 ? "99+" : unreadCount.toString();
  const label = unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : "Notificações";

  return (
    <div ref={containerRef} className="sm:relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`relative flex items-center justify-center w-6 h-6 transition-colors ${className}`}
        aria-label={label}
        title={label}
        aria-expanded={isOpen}
      >
        <FaBell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary-600 text-white text-[10px] font-bold leading-[18px] text-center ring-2 ring-white">
            {badge}
          </span>
        )}
      </button>

      {/* On phones it spans the navbar, since the bell is not at the screen's edge */}
      {isOpen && (
        <div className="absolute inset-x-4 top-full mt-2 sm:inset-x-auto sm:right-0 sm:mt-3 z-50 sm:w-[calc(100vw-2rem)] sm:max-w-sm bg-white text-gray-900 border border-gray-200 rounded-md shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="font-semibold text-sm">{"Notificações"}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
              >
                {"Marcar todas como lidas"}
              </button>
            )}
          </div>

          {latest.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">{"Nenhuma notificação por enquanto."}</p>
          ) : (
            <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto divide-y">
              {latest.map((notification) => (
                <AdminNotificationItem
                  key={notification.id}
                  notification={notification}
                  compact
                  onOpen={() => setIsOpen(false)}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              navigate("/admin#notificacoes");
            }}
            className="w-full px-4 py-3 border-t text-sm font-semibold text-primary-600 transition-colors hover:bg-gray-50"
          >
            {"Ver todas"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationBell;
